import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  BsToggles,
  BsExclamationTriangle,
  BsPencil,
  BsThreeDots,
  BsTrash,
} from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from "../confirm-modal/ConfirmModal";
import styles from "./companyPage.module.scss";
import companyPageSlice, { Company } from "./store/slice";

type CompanyForm = Partial<Omit<Company, "created_at" | "updated_at">>;

const CompanyPage = () => {
  const dispatch = useDispatch();

  const [disableCompanyId, setDisableCompanyId] = useState<number>();
  const [restrictCompanyId, setRestrictCompanyId] = useState<number>();

  const [axCode, setAxCode] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [vatCode, setVatCode] = useState<string>("");
  const [managerCode, setManagerCode] = useState<string>("");
  const [creditLimit, setCreditLimit] = useState<string>("");
  const [paymentTerm, setPaymentTerm] = useState<string>("");

  const data = useSelector((state) => state.companyPage.data);
  const meta = useSelector((state) => state.companyPage.meta);
  const isLoading = useSelector((state) => state.companyPage.isLoading);
  const isCreating = useSelector((state) => state.companyPage.isCreating);
  const isUpdating = useSelector((state) => state.companyPage.isUpdating);
  const editableIndex = useSelector((state) => state.companyPage.editableIndex);
  const search = useSelector((state) => state.companyPage.search);

  const loadMore = useCallback(
    () => dispatch(companyPageSlice.actions.loadMore()),
    [dispatch]
  );
  const create = useCallback(
    (company: CompanyForm) =>
      dispatch(companyPageSlice.actions.create(company)),
    [dispatch]
  );
  const addCompany = useCallback(
    (company: CompanyForm) =>
      dispatch(companyPageSlice.actions.addCompany(company)),
    [dispatch]
  );
  const removeCompany = useCallback(
    () => dispatch(companyPageSlice.actions.removeCompany()),
    [dispatch]
  );
  const update = useCallback(
    (company: CompanyForm) =>
      dispatch(companyPageSlice.actions.update(company)),
    [dispatch]
  );
  const remove = useCallback(
    (company: Company) => dispatch(companyPageSlice.actions.remove(company)),
    [dispatch]
  );
  const setEditableIndex = useCallback(
    (index?: number) =>
      dispatch(companyPageSlice.actions.setEditableIndex(index)),
    [dispatch]
  );
  const reset = useCallback(
    () => dispatch(companyPageSlice.actions.reset()),
    [dispatch]
  );
  const setSearch = useCallback(
    (name: string) => dispatch(companyPageSlice.actions.setSearch(name)),
    [dispatch]
  );

  useEffect(() => {
    setSearch("");

    return () => {
      reset();
    };
  }, [setSearch, reset]);

  const editMode = typeof editableIndex !== "undefined";

  return (
    <Container fluid>
      <Row>
        <Col />
        <Col lg={8}>
          <Card className={styles.card}>
            <Card.Body>
              <Card.Title className={styles.cardTitle}>
                <Container fluid className="g-0">
                  <Row className="gy-3">
                    <Col xs={12} sm={6}>
                      Companies {meta && `(${meta.total})`}
                    </Col>
                    <Col
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Form.Control
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        type="text"
                        placeholder="Search"
                        disabled={editMode}
                        style={{ marginRight: "1rem", maxWidth: "300px" }}
                      />

                      <Button
                        variant="primary"
                        disabled={editMode}
                        onClick={() => {
                          addCompany({
                            ax_code: "",
                            name: "",
                            vat_code: "",
                            manager_code: "",
                            credit_limit: "",
                            payment_term: "",
                            active: true,
                            restricted: false,
                          });

                          setAxCode("");
                          setName("");
                          setVatCode("");
                          setManagerCode("");
                          setCreditLimit("");
                          setPaymentTerm("");

                          setEditableIndex(0);
                        }}
                      >
                        Create
                      </Button>
                    </Col>
                  </Row>
                </Container>
              </Card.Title>

              <hr />

              <Table
                hover
                responsive
                striped
                borderless
                className={styles.table}
              >
                <thead>
                  <tr>
                    <th>AX code</th>
                    <th>Name</th>
                    <th>VAT code</th>
                    <th>Manager code</th>
                    <th>Credit limit</th>
                    <th>Payment term</th>
                    <th>Status</th>
                    <th>Invoice import</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!!(isLoading && data.length === 0) && (
                    <tr className={styles.loading}>
                      <td colSpan={9}>
                        <Spinner
                          className={`text-primary ${styles.initialSpinner}`}
                          animation="border"
                        />
                      </td>
                    </tr>
                  )}

                  {data.map((company, index) => {
                    const isDisabled = !!(editMode && editableIndex !== index);
                    const isActive = editableIndex === index;

                    return (
                      <tr key={index}>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={axCode}
                              onChange={(e) => setAxCode(e.target.value)}
                              size="sm"
                            />
                          ) : (
                            company.ax_code
                          )}
                        </td>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              size="sm"
                            />
                          ) : (
                            company.name
                          )}
                        </td>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={vatCode}
                              onChange={(e) => setVatCode(e.target.value)}
                              size="sm"
                            />
                          ) : (
                            company.vat_code
                          )}
                        </td>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={managerCode}
                              onChange={(e) => setManagerCode(e.target.value)}
                              size="sm"
                            />
                          ) : (
                            company.manager_code
                          )}
                        </td>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={creditLimit}
                              type="number"
                              onChange={(e) => setCreditLimit(e.target.value)}
                              size="sm"
                              min={0}
                            />
                          ) : (
                            <span>
                              {typeof company.credit_limit === "undefined" ||
                              company.credit_limit === null
                                ? "-"
                                : company.credit_limit}
                            </span>
                          )}
                        </td>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={paymentTerm}
                              type="number"
                              onChange={(e) => setPaymentTerm(e.target.value)}
                              size="sm"
                              min={0}
                            />
                          ) : (
                            <span>
                              {typeof company.payment_term === "undefined" ||
                              company.payment_term === null
                                ? "-"
                                : company.payment_term}
                            </span>
                          )}
                        </td>
                        <td className={styles.statuses}>
                          {!company.restricted && (
                            <>
                              {company.active ? (
                                <Badge bg="success">Enabled</Badge>
                              ) : (
                                <Badge bg="danger">Disabled</Badge>
                              )}
                            </>
                          )}
                          {company.restricted && (
                            <Badge bg="warning">Restricted</Badge>
                          )}
                        </td>
                        <td>
                          {company.invoice_import_enabled ? (
                            <Badge bg="info">Enabled</Badge>
                          ) : (
                            <Badge bg="secondary">Disabled</Badge>
                          )}
                        </td>
                        <td>
                          <div className={styles.actions}>
                            {isActive ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="success"
                                  className={styles.loadButton}
                                  style={{ marginRight: "0.5rem" }}
                                  onClick={() => {
                                    const body: CompanyForm = {
                                      ax_code: axCode,
                                      name,
                                      vat_code: vatCode,
                                      manager_code: managerCode,
                                      credit_limit: creditLimit,
                                      payment_term: paymentTerm,
                                      active: company.active,
                                      restricted: company.restricted,
                                    };

                                    if (
                                      editableIndex === 0 &&
                                      !data[editableIndex].id
                                    ) {
                                      create(body);
                                    } else {
                                      update({
                                        id: data[editableIndex].id,
                                        ...body,
                                      });
                                    }
                                  }}
                                >
                                  Save
                                  {!!(isCreating || isUpdating) && (
                                    <Spinner
                                      size="sm"
                                      animation="border"
                                      className={styles.spinner}
                                    />
                                  )}
                                </Button>

                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => {
                                    if (!data[editableIndex].id) {
                                      removeCompany();
                                    }

                                    setEditableIndex(undefined);
                                  }}
                                  disabled={!!(isCreating || isUpdating)}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Dropdown drop={"start"}>
                                <Dropdown.Toggle
                                  variant="outline-primary"
                                  id="dropdown-basic"
                                  size="sm"
                                  className={styles.rowToggle}
                                  disabled={isDisabled}
                                >
                                  <BsThreeDots />
                                </Dropdown.Toggle>

                                <Dropdown.Menu
                                  className={styles.rowMenu}
                                  popperConfig={{ strategy: "fixed" }}
                                  renderOnMount
                                >
                                  <Dropdown.Item
                                    onClick={() => {
                                      setAxCode(company.ax_code);
                                      setName(company.name);
                                      setVatCode(company.vat_code);
                                      setManagerCode(company.manager_code);
                                      setCreditLimit(company.credit_limit);
                                      setPaymentTerm(company.payment_term);
                                      setEditableIndex(index);
                                    }}
                                    disabled={isDisabled}
                                  >
                                    <BsPencil /> Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => remove(company)}
                                    disabled={isDisabled}
                                  >
                                    <BsTrash /> Delete
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  {!company.restricted && (
                                    <>
                                      {company.active ? (
                                        <Dropdown.Item
                                          disabled={isDisabled}
                                          onClick={() =>
                                            setDisableCompanyId(company.id)
                                          }
                                        >
                                          <BsToggles /> Disable
                                        </Dropdown.Item>
                                      ) : (
                                        <Dropdown.Item
                                          disabled={isDisabled}
                                          onClick={() => {
                                            update({
                                              id: company.id,
                                              active: true,
                                            });
                                          }}
                                        >
                                          <BsToggles /> Enable
                                        </Dropdown.Item>
                                      )}
                                    </>
                                  )}
                                  {company.active && (
                                    <>
                                      {company.restricted ? (
                                        <Dropdown.Item
                                          disabled={isDisabled}
                                          onClick={() => {
                                            update({
                                              id: company.id,
                                              restricted: false,
                                            });
                                          }}
                                        >
                                          <BsExclamationTriangle /> Remove
                                          restrictions
                                        </Dropdown.Item>
                                      ) : (
                                        <Dropdown.Item
                                          disabled={isDisabled}
                                          onClick={() =>
                                            setRestrictCompanyId(company.id)
                                          }
                                        >
                                          <BsExclamationTriangle /> Restrict
                                        </Dropdown.Item>
                                      )}
                                    </>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {meta && (
                <div className={styles.loadMore}>
                  {meta.current_page < meta.last_page ? (
                    <Button onClick={() => loadMore()} disabled={editMode}>
                      Load more
                      {isLoading && (
                        <Spinner
                          size="sm"
                          animation="border"
                          className={styles.spinner}
                        />
                      )}
                    </Button>
                  ) : data.length > 10 ? (
                    <span className="text-muted">Nothing more to show.</span>
                  ) : null}
                </div>
              )}
            </Card.Body>
          </Card>

          <ConfirmModal
            question={`Disable company?`}
            submitText="Disable"
            submitVariant="danger"
            isVisible={!!disableCompanyId}
            onHide={() => {
              setDisableCompanyId(undefined);
            }}
            body={
              <div>
                <p className="text-danger">
                  <strong>
                    All current company bookings will be cancelled.
                  </strong>
                </p>
                <p>
                  Company users will not be able to login and make changes to
                  existing bookings.
                </p>
              </div>
            }
            onSubmit={() => {
              update({
                id: disableCompanyId,
                active: false,
              });

              setDisableCompanyId(undefined);
            }}
          />

          <ConfirmModal
            question={`Restrict company?`}
            submitText="Restrict"
            submitVariant="danger"
            isVisible={!!restrictCompanyId}
            onHide={() => {
              setRestrictCompanyId(undefined);
            }}
            body={
              <div>
                <p className="text-danger">
                  <strong>
                    Users will not be able to create new bookings.
                  </strong>
                </p>
                <p>All current company bookings will be still available.</p>
              </div>
            }
            onSubmit={() => {
              update({
                id: restrictCompanyId,
                restricted: true,
              });

              setRestrictCompanyId(undefined);
            }}
          />
        </Col>
        <Col />
      </Row>
    </Container>
  );
};

export default CompanyPage;
